import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../utils/api";
import { useToast } from "../context/ToastContext";

// Hand a live chat to one agent (works even when they are Away / at their limit: shown as a warning) or to a
// group (the routing rules pick an online agent there, otherwise the chat waits in the queue).
const ReassignChat = ({ sessionId, aiAgentId, onClose, onDone }) => {
  const { showToast } = useToast();
  const [team, setTeam] = useState(null);
  const [groups, setGroups] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/live-chat/agents", { aiAgentId, skipToast: true }).then(setTeam).catch(() => setTeam({ agents: [] }));
    api.get("/human-agent-groups", { skipToast: true }).then((r) => setGroups(r?.groups || [])).catch(() => {});
  }, [aiAgentId]);

  const assign = async (body, label) => {
    setBusy(true);
    try {
      const res = await api.post(`/live-chat/sessions/${sessionId}/assign`, body, { aiAgentId });
      if (res?.status === "active") {
        const warn = res.online === false ? " (agent is Away)" : res.over_limit ? " (agent is over the chat limit)" : "";
        showToast(`Chat assigned to ${res.assigned_to_name}${warn}`, warn ? "warning" : "success");
      } else {
        showToast(`Moved to ${label}: waiting for an available agent`, "success");
      }
      onDone && onDone(res);
      onClose();
    } catch (e) {
      // the API client shows the server's message
    } finally {
      setBusy(false);
    }
  };

  const row = { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", padding: "8px 10px",
    border: "1px solid #e4e4e7", borderRadius: "8px", background: "#fff", cursor: busy ? "wait" : "pointer", fontSize: "12px",
    color: "#18181b", textAlign: "left" };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1000,
      display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "#fff", borderRadius: "14px", width: "min(380px, 92vw)",
        maxHeight: "80vh", overflow: "auto", padding: "18px", boxShadow: "0 10px 30px rgba(0,0,0,0.18)" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
          <div style={{ fontWeight: 700, fontSize: "14px", color: "#18181b" }}>Reassign chat</div>
          <button type="button" onClick={onClose} style={{ border: "none", background: "none", cursor: "pointer" }} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div style={{ fontSize: "10px", fontWeight: 700, color: "#71717a", textTransform: "uppercase", marginBottom: "6px" }}>Agent</div>
        <div style={{ display: "flex", flexDirection: "column", gap: "6px", marginBottom: "14px" }}>
          {!team && <div style={{ fontSize: "12px", color: "#a1a1aa" }}>Loading...</div>}
          {team && team.agents.length === 0 && <div style={{ fontSize: "12px", color: "#a1a1aa" }}>No agents for this bot</div>}
          {team && team.agents.map((a) => (
            <button key={a.user_id} type="button" disabled={busy} style={row}
              onClick={() => assign({ user_id: a.user_id }, a.name)}>
              <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: a.online ? "#10b981" : "#d4d4d8" }} />
                {a.name}{a.is_me ? " (you)" : ""}
                {!a.online && <span style={{ color: "#a1a1aa", fontSize: "11px" }}>Away</span>}
              </span>
              <span style={{ color: a.at_capacity ? "#b45309" : "#a1a1aa", fontSize: "11px", fontWeight: 600 }}>
                {a.open_chats}{a.max_chats ? `/${a.max_chats}` : ""} chats
              </span>
            </button>
          ))}
        </div>

        {groups.length > 0 && (
          <>
            <div style={{ fontSize: "10px", fontWeight: 700, color: "#71717a", textTransform: "uppercase", marginBottom: "6px" }}>
              Group (next available agent)
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              {groups.map((g) => (
                <button key={g.group_id} type="button" disabled={busy} style={row}
                  onClick={() => assign({ group_id: g.group_id }, g.name)}>
                  <span>{g.name}</span>
                  <span style={{ color: "#a1a1aa", fontSize: "11px" }}>{(g.human_agent_ids || []).length} members</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ReassignChat;
